import { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail } from 'lucide-react';
import { initializeFirebase, signInWithGoogle } from '../config/firebase';
import StudentOnboarding from '../components/StudentOnboarding';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const { login } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');

  // Initialize Firebase on component mount
  useEffect(() => {
    initializeFirebase();
  }, []);
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.target);
    
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: formData.get('email'),
        password: formData.get('password')
      });
      login(res.data.token, res.data.user);
      toast.success('Login successful!');
      // Role-based redirect
      if (res.data.user.role === 'teacher') {
        // Check if teacher is a school admin — redirect accordingly
        try {
          const schoolId = res.data.user.school_id;
          if (schoolId) {
            const check = await axios.get(`${API}/schools/${schoolId}/school-admin/dashboard`, {
              headers: { Authorization: `Bearer ${res.data.token}` }
            });
            if (check.data?.school) {
              navigate('/school-admin');
              return;
            }
          }
        } catch { /* Not a school admin, proceed to teacher dashboard */ }
        navigate('/teacher/dashboard');
      } else if (res.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.target);
    
    // Validate password confirmation
    if (formData.get('password') !== formData.get('confirm_password')) {
      toast.error('Passwords do not match');
      setIsLoading(false);
      return;
    }
    
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: formData.get('email'),
        password: formData.get('password'),
        name: formData.get('name'),
        role: formData.get('role')
      });
      login(res.data.token, res.data.user);
      toast.success('Registration successful!');
      
      // Apply referral code if present
      if (refCode) {
        try {
          await axios.post(`${API}/referral/apply`, { code: refCode }, {
            headers: { Authorization: `Bearer ${res.data.token}` }
          });
          toast.success(`Referral code applied! You earned bonus points.`);
        } catch { /* silently ignore referral errors */ }
      }
      
      // Role-based redirect
      if (res.data.user.role === 'teacher') {
        navigate('/teacher/dashboard');
      } else {
        // Show onboarding for students
        setPendingRedirect('/');
        setShowOnboarding(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Firebase Google Sign-In
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      // Sign in with Firebase Google popup
      const result = await signInWithGoogle();
      
      if (result.success) {
        // Send Firebase ID token to backend for verification/registration
        const res = await axios.post(`${API}/auth/firebase-login`, {
          id_token: result.idToken,
          email: result.user.email,
          name: result.user.displayName,
          photo_url: result.user.photoURL
        });
        
        login(res.data.token, res.data.user);
        toast.success('Login successful!');
        
        // Role-based redirect
        if (res.data.user.role === 'teacher') {
          navigate('/teacher/dashboard');
        } else if (res.data.user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/');
        }
      } else {
        toast.error(result.error || 'Google sign-in failed');
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error(error.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length < 9) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/phone/request-otp`, { phone: phoneNumber });
      setOtpSent(true);
      toast.success(`OTP sent to ${res.data.phone}`);
      if (res.data.is_new_user) {
        toast.info('New account will be created automatically');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    }
    setIsLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/phone/verify-otp`, { 
        phone: phoneNumber, 
        otp: otp 
      });
      login(res.data.token, res.data.user);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 flex items-center justify-center p-4">
      {showOnboarding && (
        <StudentOnboarding onComplete={() => {
          setShowOnboarding(false);
          navigate(pendingRedirect || '/');
        }} />
      )}
      <div className="w-full max-w-md flex flex-col items-center">
      <Card className="w-full" data-testid="login-card">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-black">Welcome to Darasani</CardTitle>
          <CardDescription>Access world-class education</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Social Login Buttons */}
          <div className="space-y-3 mb-6">
            <Button 
              type="button" 
              variant="outline" 
              className="w-full h-12 border-slate-300 hover:bg-slate-50"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            
            <Button 
              type="button" 
              variant="outline" 
              className="w-full h-12 border-slate-300 hover:bg-slate-50"
              onClick={() => setShowPhoneLogin(!showPhoneLogin)}
              data-testid="phone-login-btn"
            >
              <Phone className="w-5 h-5 mr-3 text-green-600" />
              Continue with Phone
            </Button>
          </div>

          {/* Phone Login Section */}
          {showPhoneLogin && (
            <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Phone Number</Label>
              {!otpSent ? (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input 
                      type="tel"
                      placeholder="+255 7XX XXX XXX"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="flex-1"
                      data-testid="phone-input"
                    />
                  </div>
                  <Button 
                    type="button" 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleSendOtp}
                    disabled={isLoading}
                    data-testid="send-otp-btn"
                  >
                    {isLoading ? 'Sending...' : 'Send OTP'}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    We'll send a 6-digit OTP to verify your number
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Input 
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                    data-testid="otp-input"
                  />
                  <Button 
                    type="button" 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleVerifyOtp}
                    disabled={isLoading}
                    data-testid="verify-otp-btn"
                  >
                    {isLoading ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                  <button 
                    type="button"
                    className="text-sm text-indigo-600 hover:underline w-full"
                    onClick={() => setOtpSent(false)}
                  >
                    Change phone number
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">Or continue with email</span>
            </div>
          </div>

          <Tabs defaultValue={refCode ? "register" : "login"} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input 
                    id="login-email" 
                    name="email" 
                    type="email" 
                    placeholder="you@example.com" 
                    required 
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input 
                    id="login-password" 
                    name="password" 
                    type="password" 
                    placeholder="••••••••" 
                    required 
                    data-testid="login-password-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-submit-btn">
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">Full Name</Label>
                  <Input 
                    id="register-name" 
                    name="name" 
                    placeholder="John Doe" 
                    required 
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email">Email</Label>
                  <Input 
                    id="register-email" 
                    name="email" 
                    type="email" 
                    placeholder="you@example.com" 
                    required 
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">Password</Label>
                  <Input 
                    id="register-password" 
                    name="password" 
                    type="password" 
                    placeholder="••••••••" 
                    required 
                    minLength={6}
                    data-testid="register-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-confirm-password">Confirm Password</Label>
                  <Input 
                    id="register-confirm-password" 
                    name="confirm_password" 
                    type="password" 
                    placeholder="••••••••" 
                    required 
                    minLength={6}
                    data-testid="register-confirm-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-role">I am a...</Label>
                  <Select name="role" required defaultValue="student">
                    <SelectTrigger data-testid="register-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student" data-testid="role-student">Student</SelectItem>
                      <SelectItem value="teacher" data-testid="role-teacher">Teacher</SelectItem>
                      <SelectItem value="book_provider" data-testid="role-book-provider">Book Provider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {refCode && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <p className="text-xs text-indigo-600 font-medium">Referral Code Applied</p>
                    <p className="text-sm text-indigo-800 font-bold">{refCode}</p>
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={isLoading} data-testid="register-submit-btn">
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      <div className="mt-4 text-center space-y-3" data-testid="register-school-cta">
        <button
          onClick={() => {
            const token = localStorage.getItem('token');
            if (token) {
              navigate('/school-admin');
            } else {
              toast.info('Please login first, then you will be redirected to the School Admin panel');
            }
          }}
          className="w-full max-w-[280px] mx-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 hover:border-amber-300 transition-all text-sm font-semibold"
          data-testid="school-admin-login-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          School Manager Portal
        </button>
        <div>
          <p className="text-sm text-slate-500 mb-1">Want to register your school?</p>
          <button
            onClick={() => {
              const token = localStorage.getItem('token');
              if (token) {
                navigate('/register-school');
              } else {
                toast.info('Please login or create an account first to register your school');
              }
            }}
            className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 underline underline-offset-2 transition-colors"
            data-testid="register-school-link"
          >
            Register a School
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default Login;
